/* eslint-disable react/no-this-in-sfc */
/* eslint-disable indent */
import React, { useContext, useEffect, useState } from 'react';
import {
  Typography,
  useTheme,
  Snackbar,
  useMediaQuery,
} from '@material-ui/core';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { pick, flatten, cloneDeep } from 'lodash-es';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import moment from 'moment-timezone';
import { isSmallTouchDevice } from '@mummssoftware/utils/web';
import { Loader } from '@mummssoftware/common-ui';
import { NoteCountCellRenderer } from './NoteCountRenderer';
import {
  getDateRange,
  getMeasuresForGraph,
  getTypesByName,
  orderVitalsMeasureNames,
} from '../../MeasuresContext/utils/utils';
import { AllMeasuresDefined } from '../../MeasuresContext/utils/type';
import { MeasuresContext } from '../../MeasuresContext/MeasuresContextProvider';
import {
  chartOptions,
  fastCategory,
  reverseFastMap,
  nyhaCategory,
  reverseNyhaMap,
} from './chart-data';
import {
  chartHeightRatio,
  SET_MEASURE_SELECTED,
  SET_MEASURE_NAME,
  SET_NEW_MEASURE,
  SET_SCORES_SUBCATEGORY,
  SET_TABLE_NAME,
  SHOW_TABLE,
  MEASURE_TYPE,
  scoresType,
  objective,
  subjective,
  filterUiVisibleMeasureTypes,
  getMeasureDescriptionFromMaps,
} from '../../MeasuresContext/utils/constants';
import {
  ScoresNameDiv,
  MeasureNameDiv,
  MeasuresWrapper,
  ChartWrapper,
  MainGraph,
  ScoreNameFocused,
  StyledScoresUIDiv,
  ScoreName,
  ChartNameWrap,
  DividerActionDiv,
  NotCountWrapper,
} from '../../../components/layout/WrapperDiv';
import defaults from '../../../translations/en.json';
import { ScoresNumbers, ScoresMethods } from '../../../components/scores';
import { Context } from '../../../context';
import { VitalsForm } from '../../../components/vitals/VitalsForm';
import { MeasuresForm } from '../../../components/measures-not used/MeasuresForm';

import { Divider } from './Divider';
import { MeasuresHistoryPreview } from './MeasuresHistoryPreview';

require('highcharts/modules/data')(Highcharts);
require('highcharts/modules/no-data-to-display')(Highcharts);
require('highcharts/modules/series-label')(Highcharts);

type MeasuresFullGraphProps = {
  allMeasures: AllMeasuresDefined;
  smallWidget?: boolean;
  height: number;
  siteTimezone?: string | null;
  episodes: mumms.episode[];
};
const texts: NonNullable<mumms.JSONObject> = defaults;

// NOTES: the alert takes care of clearing error messages => handleErrorMsg
// that is why not done in respective scores and measures forms

export const MeasuresFullGraph = React.memo(
  ({
    allMeasures,
    smallWidget,
    height,
    siteTimezone,
    episodes,
  }: MeasuresFullGraphProps) => {
    const {
      palette: {
        graph: { border, graphColor, secondGraphColor },
      },
    } = useTheme() as any;
    // TODO use fontColor
    const {
      dispatch,
      newMeasure,
      nameSelected,
      measureSelected,
      methodSelected,
      isLoading,
      tableSelected,
    } = useContext(MeasuresContext);
    const {
      isPatientActive,
      currentUser,
      handleErrorMsg,
      readOnly,
      measuresTypesMap,
      patient,
    } = useContext(Context);
    const isNarrowPhoneView = useMediaQuery('(max-width:600px)', {
      noSsr: true,
    });
    const isIPadLikeTouch = isSmallTouchDevice();
    const {
      measures,
      measuresTypes,
      allMeasuresTypes,
      alertError,
      alertSuccess,
      alertInfo,
      measuresMapName,
    } = allMeasures;
    const measureType = localStorage.getItem(MEASURE_TYPE);
    const isScores = measureType === scoresType;

    const scoresMetrics: any = filterUiVisibleMeasureTypes(
      allMeasuresTypes?.[scoresType],
    );
    const scoresCategory: mumms.ScoresCategory =
      scoresMetrics &&
      [...scoresMetrics].reduce((seed: any, score: any) => {
        seed[score.shortname] = score.subcategory;
        return seed;
      }, {});

    // todo should be done in Context in index.tsx
    const scoresSubcategory: mumms.SubcategoryType =
      scoresMetrics &&
      [...scoresMetrics].reduce((seed: any, score: any) => {
        if (!seed[score.subcategory]) {
          seed[score.subcategory] = [score.shortname];
        } else {
          seed[score.subcategory].push(score.shortname);
        }
        return seed;
      }, {});

    useEffect(() => {
      scoresSubcategory &&
        dispatch({
          type: SET_SCORES_SUBCATEGORY,
          payload: scoresSubcategory,
        });
    }, []);

    const [measuresTypesState, setMeasuresTypesState] = useState<
      mumms.measureType[]
    >(measuresTypes);

    useEffect(() => {
      Highcharts.charts.forEach((chart) => {
        chart && chart.reflow();
      });
    }, [methodSelected]);

    // if we have not selected a specific measure nor adding a new measure then all measures shown
    useEffect(() => {
      if (!measureSelected && !newMeasure) {
        setMeasuresTypesState(measuresTypes);
      }
    }, [measuresTypes, measureSelected, newMeasure]);

    useEffect(() => {
      setMeasuresTypesState(measuresTypes);
    }, [isScores, measuresTypes]);

    // *****ALERT******
    const [alertMessage, setAlertMessage] = useState<string | null | undefined>(
      alertError || alertSuccess || alertInfo,
    );

    useEffect(() => {
      const alert = alertError || alertSuccess || alertInfo;
      setAlertMessage(alert);
    }, [alertError, alertSuccess, alertInfo]);

    const [alertOpen, setAlertOpen] = useState<boolean>(!!alertMessage);
    useEffect(() => {
      setAlertOpen(!!alertMessage);
    }, [alertMessage, dispatch, handleErrorMsg]);

    useEffect(() => {
      if (alertMessage) {
        setTimeout(() => {
          setAlertOpen(false);
          handleErrorMsg();
        }, 1000);
      }
    }, [alertMessage, handleErrorMsg]);
    // *****END OF ALERT******

    // allMeasuresPicked = {bp: [data], weight: [data]}
    let allMeasuresPicked: any;
    let allCategoryMeasuresPicked: any = {};
    let sortedScoresTypes;
    let allActiveMeasuresTypes;

    // only for SCORES
    if (isScores) {
      // not mutate measuresTypes directly
      const inUseObjectiveMeasures = [...measuresTypesState].filter(
        (m) => m.subcategory === objective && m.isinuse === 'true',
      );
      const notInUseObjectiveMeasures = [...measuresTypesState].filter(
        (m) => m.subcategory === objective && m.isinuse === 'false',
      );
      const objectiveMeasures = [
        ...notInUseObjectiveMeasures,
        ...inUseObjectiveMeasures,
      ];
      const subjectiveMeasures = [...measuresTypesState].filter(
        (m) => m.subcategory === subjective,
      );
      /** Sort by Objective last */
      sortedScoresTypes = [...subjectiveMeasures, ...objectiveMeasures];

      allMeasuresPicked = sortedScoresTypes.reduce((seed, val) => {
        if (val.shortname) {
          const shortName = val.shortname;
          (seed as any)[shortName] = (measures as any)[shortName] || [];
        }
        return seed;
      }, {});
    } else {
      const allCategoryMeasureNames = measuresTypesState.map(
        (measure) => measure.shortname,
      );

      allActiveMeasuresTypes = measuresTypesState;
      // Include non-graphable types (e.g. ECOG) so name rows / table access remain available.
      allMeasuresPicked = pick(measures, allCategoryMeasureNames);
      allCategoryMeasureNames.forEach((measureName) => {
        if (!Object.keys(allMeasuresPicked).includes(measureName)) {
          allMeasuresPicked[measureName] = [];
        }
      });
      allCategoryMeasuresPicked = allMeasuresPicked;
    }

    const measuresSelectedArr: any = Object.values(allMeasuresPicked);
    const allMeasuresFordate: any = flatten(measuresSelectedArr);
    const dateRange = getDateRange(allMeasuresFordate);
    const typeNames: Record<
      mumms.measureShortNames,
      mumms.measureType
    > = getTypesByName(measuresTypesState);

    // Highcharts does not find moment when loaded as a module using import
    // https://github.com/highcharts/highcharts/issues/8661
    (window as any).moment = moment;

    /*
     * We are using the measuredatetimeshifted and visitdatetimeshifted values  throughout
     * this widget which already has the timezone offset applied to it.
     * HighChart accepts epoch time => UTC time
     */
    const tz = currentUser.siteTimezone || moment.tz.guess();
    moment.tz.setDefault(tz);
    Highcharts.setOptions({
      time: {
        timezone: tz,
      },
    });

    /**
     * variables for graph width and height small widget and not
     */
    const maxFullSizeChartHeight = 185;

    const fullChartSize = 140;
    const fullChartHeight = Math.min(fullChartSize, maxFullSizeChartHeight);

    const [fullSizeChartHeight, setFullSizeChartHeight] = useState<number>(
      fullChartHeight,
    );

    // // chart height to update when view switches from stacked graphs to single graph
    useEffect(() => {
      if (!measureSelected && !newMeasure) {
        setFullSizeChartHeight(fullChartHeight);
      }
    }, [measureSelected, fullChartHeight, newMeasure]);

    const chartHeight = fullSizeChartHeight;

    const variant = 'body1';

    const fullSizeChart = chartHeightRatio * height;

    const setMeasure = (name: string) => {
      const isGraphable = typeNames[name]?.isgraphable === 'true';
      if (!isGraphable) {
        if (tableSelected === name) {
          dispatch({
            type: SET_TABLE_NAME,
            payload: null,
          });
        } else {
          dispatch({
            type: SHOW_TABLE,
            payload: false,
          });
          dispatch({
            type: SET_MEASURE_NAME,
            payload: null,
          });
          dispatch({
            type: SET_MEASURE_SELECTED,
            payload: null,
          });
          dispatch({
            type: SET_NEW_MEASURE,
            payload: false,
          });
          dispatch({
            type: SET_TABLE_NAME,
            payload: name,
          });
        }
        return;
      }

      dispatch({
        type: SET_MEASURE_SELECTED,
        payload: name,
      });
      const measure = measuresTypes.filter((m) => m.shortname === name);
      // set the state to only the measure selected
      // adjust the size of the graph to individual size
      setMeasuresTypesState(measure);
      setFullSizeChartHeight(fullSizeChart);

      // when double clicking on name selected hide or show
      // think about adding a new measure as well
      if (name !== nameSelected && !newMeasure) {
        dispatch({
          type: SHOW_TABLE,
          payload: true,
        });
        dispatch({
          type: SET_MEASURE_NAME,
          payload: name,
        });
      } else {
        dispatch({
          type: SHOW_TABLE,
          payload: false,
        });
        dispatch({
          type: SET_MEASURE_NAME,
          payload: null,
        });
        dispatch({
          type: SET_MEASURE_SELECTED,
          payload: null,
        });
        dispatch({
          type: SET_NEW_MEASURE,
          payload: false,
        });
      }
    };

    // function to get access to this
    function formatter(this: any) {
      // special tooltip for fast
      const y = (reverseFastMap as any)[this.y];
      const text = `<br/><span style="color:${this.color}">\u25CF</span> ${this.series.name}: ${y}`;
      return text;
    }

    function nyhaFormatter(this: any) {
      const y = (reverseNyhaMap as any)[this.y];
      const text = `<br/><span style="color:${this.color}">\u25CF</span> ${this.series.name}: ${y}`;
      return text;
    }

    const getSingleChartUI = (
      chartmeasures: mumms.measure[],
      name: mumms.measureShortNames,
      index: number,
    ) => {
      const isGraphable = typeNames[name]?.isgraphable === 'true';
      if (!isGraphable) {
        const isInlineTableOpen = tableSelected === name && !measureSelected;
        return (
          <ChartNameWrap key={index} id={`single-measure-${name}`}>
            {getSingleNameUI(name)}
            {!isInlineTableOpen && (
              <MeasuresHistoryPreview
                measures={chartmeasures}
                chartHeight={chartHeight}
                border={border}
                smallWidget={smallWidget}
              />
            )}
            {!measureSelected && (
              <div
                style={isNarrowPhoneView ? { visibility: 'hidden' } : undefined}
              >
                <Divider allMeasures={allMeasures} name={name} />
              </div>
            )}
          </ChartNameWrap>
        );
      }

      const show2ndData = typeNames[name].isgraphwithsecondaryval === 'true';
      const options: any = cloneDeep(chartOptions(border));
      options.chart.height = chartHeight;
      options.chart.marginLeft = smallWidget ? 0 : 60;
      (options.yAxis as any).visible = !smallWidget;

      let measureValues: any;

      // FAST category
      if (name === 'fast') {
        (options.yAxis as any).categories = fastCategory;
        measureValues = getMeasuresForGraph(
          chartmeasures,
          dateRange,
          'value',
          'fast',
        );
        (options.tooltip as any).pointFormatter = formatter;
      } else if (name === 'nyha') {
        options.chart.marginLeft = smallWidget ? 0 : 70;
        (options.yAxis as any).categories = nyhaCategory;
        measureValues = getMeasuresForGraph(
          chartmeasures,
          dateRange,
          'value',
          'nyha',
        );
        (options.tooltip as any).pointFormatter = nyhaFormatter;
      } else {
        measureValues = getMeasuresForGraph(chartmeasures, dateRange, 'value');
      }
      if (episodes && episodes.length > 1 && measureSelected) {
        const plotBands: any[] = [];
        for (let i = 0; i < episodes.length - 1; i++) {
          plotBands.push({
            color: border,
            from: episodes[i].utcDischargeDate,
            to: episodes[i + 1].utcReferralDate,
            zIndex: 5,
          });
        }
        (options.xAxis as any).plotBands = plotBands;
      } else {
        (options.xAxis as any).plotBands = null;
      }

      (options.xAxis as any).visible = true;

      const firstSerie = {
        id: name,
        data: measureValues,
        name,
        type: 'line',
        color: graphColor,
        fillOpacity: 0.3,
      };
      options.series = [firstSerie];

      if (show2ndData) {
        // backend value2 to graph 2nd value
        const measureValues2 = getMeasuresForGraph(
          chartmeasures,
          dateRange,
          'value2',
        );
        const secondSerie = {
          id: 'other',
          data: measureValues2,
          name,
          type: 'line',
          color: secondGraphColor,
          fillOpacity: 0.3,
        };
        options.series = [firstSerie, secondSerie];
      }

      // function to get access to this
      function smallWidgetFormatter(this: any) {
        let { y } = this;
        if (name === 'fast') {
          y = (reverseFastMap as any)[this.y];
        } else if (name === 'nyha') {
          y = (reverseNyhaMap as any)[this.y];
        }
        return `${y}`;
      }

      if (smallWidget) {
        (options as any).tooltip = {
          borderWidth: 1,
          pointFormatter: smallWidgetFormatter,
          headerFormat: '',
          crosshairs: [true],
        };
      }
      // had put a key in hoping it would trigger a HighChart re-render
      return (
        <ChartNameWrap key={index} id={`single-measure-${name}`}>
          {getSingleNameUI(name)}
          <HighchartsReact
            key={index}
            highcharts={Highcharts}
            options={options as any}
          />
          {!measureSelected && (
            <div
              style={isNarrowPhoneView ? { visibility: 'hidden' } : undefined}
            >
              <Divider allMeasures={allMeasures} name={name} />
            </div>
          )}
        </ChartNameWrap>
      );
    };

    const getOrderedNames = (chartmeasures: mumms.formattedAllMeasures) => {
      if (!chartmeasures || !Object.keys(chartmeasures).length) {
        return [];
      }
      return orderVitalsMeasureNames(Object.keys(chartmeasures));
    };

    const getChartUI = (chartmeasures: mumms.formattedAllMeasures) => {
      if (chartmeasures && Object.keys(chartmeasures).length) {
        return getOrderedNames(chartmeasures).map((name, index) =>
          getSingleChartUI((chartmeasures as any)[name], name, index),
        );
      }
      return null;
    };

    const getSingleNameUI = (name: mumms.measureShortNames) => {
      const description = getMeasureDescriptionFromMaps(
        name,
        measuresMapName,
        typeNames[name],
      );

      const showScoresNumbers = isPatientActive && isScores && !readOnly;
      const nameText =
        isNarrowPhoneView || isIPadLikeTouch
          ? name.toUpperCase()
          : `${name.toUpperCase()} - ${description}`;
      const fullSizeName = methodSelected ? (
        <ScoreName>{nameText}</ScoreName>
      ) : (
        <ScoreNameFocused onClick={() => setMeasure(name)}>
          {nameText}
        </ScoreNameFocused>
      );
      const typeMap = measuresTypesMap ? measuresTypesMap[name] : {};
      return (
        <DividerActionDiv>
          <NotCountWrapper>
            <NoteCountCellRenderer data={{ ...typeMap, patient }} />
          </NotCountWrapper>
          <ScoresNameDiv key={name} color={border} id={`score-name-${name}`}>
            <MeasureNameDiv
              smallWidget={smallWidget}
              showScoreForm={!!methodSelected}
            >
              {fullSizeName}
            </MeasureNameDiv>
            {showScoresNumbers && (
              <ScoresNumbers name={name} scoresCategory={scoresCategory} />
            )}
          </ScoresNameDiv>
        </DividerActionDiv>
      );
    };

    const chartWrapperId = 'chart-full';
    // todo better handling of this
    const headerHeight = 30;
    const expandHeight = 40;

    const showSubmitVitals =
      !measureSelected && !isScores && isPatientActive && !readOnly;
    const showSubmitScores =
      !measureSelected && isScores && isPatientActive && !readOnly;

    // *****Alert saving measures****
    const Alert = (props: AlertProps) => (
      <MuiAlert elevation={6} variant="filled" {...props} />
    );
    const open = alertOpen;
    const handleClose = () => setAlertOpen(false);

    // eslint-disable-next-line no-nested-ternary
    return !Object.values(allMeasuresPicked).length && !newMeasure ? (
      <Typography variant={variant}>{texts.noMeasures}</Typography>
    ) : (
      <MainGraph id="main-graph" isScores={isScores || !isPatientActive}>
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert
            onClose={handleClose}
            // eslint-disable-next-line no-nested-ternary
            severity={alertInfo ? 'info' : alertError ? 'error' : 'success'}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
        {isLoading && <Loader />}
        {showSubmitScores && <ScoresMethods />}
        <MeasuresWrapper
          id="graph-area"
          smallWidget={smallWidget}
          key={`${isScores.toString()}-${measureSelected}`}
          // openMeasures={!isScores && !measureSelected}
        >
          <ChartWrapper id={chartWrapperId}>
            {getChartUI(allMeasuresPicked)}
          </ChartWrapper>
        </MeasuresWrapper>
        {showSubmitVitals && (
          <StyledScoresUIDiv id="measures-form-full-graph">
            <VitalsForm
              chartHeight={chartHeight + headerHeight + expandHeight}
              measuresTypes={allActiveMeasuresTypes as mumms.measureType[]}
              measuresNames={getOrderedNames(allCategoryMeasuresPicked)}
              key={alertMessage ? 'key1' : 'key0'}
            />
          </StyledScoresUIDiv>
        )}
      </MainGraph>
    );
  },
);

export default MeasuresFullGraph;
